import { useEffect, useRef, useState } from 'react'

type OnLoadCallback = () => void

/**
 * Google Maps api script loading is deferred, which means our react app and
 * its component may load and mount before the google maps api is available
 * under the `google` global variable. This hooks returns a ref with a boolean
 * value reflecting whether the google script has loaded yet or not.
 */

const useGoogleMapsScriptLoadedRef = (onLoad?: OnLoadCallback) => {
  const loaded = useRef(false)

  if (window.google) {
    loaded.current = true
    if (onLoad) {
      onLoad()
    }
  }

  const onLoadListener = () => {
    loaded.current = true
    if (onLoad) {
      onLoad()
    }
  }

  useEffect(() => {
    if (loaded.current !== true) {
      // this ID attribute is set in application.html.slim
      const googleScript = document.getElementById('google-maps-script')
      googleScript.addEventListener('load', onLoadListener)
    }

    return () => {
      const googleScript = document.getElementById('google-maps-script')
      googleScript.removeEventListener('load', onLoadListener)
    }
  }, [])

  return loaded
}

export const useGoogleMapsScriptReady = () => {
  const loadedRef = useGoogleMapsScriptLoadedRef()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (loadedRef.current === true) {
      setReady(true)
    }
  }, [loadedRef.current])

  return ready
}

export default useGoogleMapsScriptLoadedRef
